import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

// Enhanced Schedule details interface to match ScheduleCreativePage structure
interface PlaylistScheduleDetails {
  scheduleType: "now" | "later" | "selection";
  announcement: "general" | "tag-level";
  scheduleData: {
    selectedSlots?: Set<string> | string[];
    duration?: { value: string; label: string } | string;
    startDate?: Date | string;
    endDate?: Date | string;
  };
  announcementDetails?: {
    selectedTags?: string[];
    selectedLocations?: string[];
    totalCreatives?: number;
    creativeTitles?: string[];
    selectedValues?: string[];
    availableValues?: Array<{ id: string; label: string }>;
    [key: string]: any;
  };
  scheduledAt?: string; // When the playlist was scheduled
  dateRange?: string; // Formatted date range string
  locations?: string; // Formatted locations string
}

export interface Playlist {
  id: string;
  title: string;
  creativesCount: number;
  imageUrl?: string; // Make optional since we'll compute it dynamically
  status: "Playing" | "Draft Saved";
  isScheduled?: boolean;
  scheduledAt?: string;
  createdAt: Date;
  updatedAt: Date;
  // Add creative data to playlist
  creativeIds: string[]; // Store creative IDs in playlist
  creativeDurations: Record<string, number>; // Store custom durations
  // Enhanced schedule details
  scheduleDetails?: PlaylistScheduleDetails;
}

interface PlaylistContextType {
  playlists: Playlist[];
  addPlaylist: (title: string) => Playlist;
  updatePlaylist: (id: string, updates: Partial<Playlist>) => void;
  deletePlaylist: (id: string) => void;
  getPlaylistById: (id: string) => Playlist | undefined;
  schedulePlaylist: (id: string, scheduleDetails?: PlaylistScheduleDetails) => void;
  // Add methods for managing playlist creatives
  updatePlaylistCreatives: (
    id: string,
    creativeIds: string[],
    creativeDurations?: Record<string, number>,
    allCreatives?: any[] // Add allCreatives parameter
  ) => void;
  // Add method to get playlist with creative details
  getPlaylistWithCreatives: (id: string, allCreatives: any[]) => {
    playlist: Playlist | undefined;
    creativesWithDurations: any[];
  };
  // Add method to get playlist with dynamic image
  getPlaylistWithImage: (id: string, allCreatives: any[]) => Playlist | undefined;
  // Add method to clear all playlists
  clearAllPlaylists: () => void;
}

const PlaylistContext = createContext<PlaylistContextType | undefined>(
  undefined
);

export const usePlaylist = () => {
  const context = useContext(PlaylistContext);
  if (!context) {
    throw new Error("usePlaylist must be used within a PlaylistProvider");
  }
  return context;
};

interface PlaylistProviderProps {
  children: ReactNode;
}

// Storage key for localStorage
const STORAGE_KEY = "playlists";

// Helper functions for localStorage operations
const loadFromStorage = (): Playlist[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that the data has the expected structure
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (
              item &&
              typeof item === "object" &&
              typeof item.id === "string" &&
              typeof item.title === "string"
            ) {
              // Convert selectedSlots array back to Set if it exists in scheduleDetails
              if (
                item.scheduleDetails?.scheduleData?.selectedSlots &&
                Array.isArray(item.scheduleDetails.scheduleData.selectedSlots)
              ) {
                item.scheduleDetails.scheduleData.selectedSlots = new Set(
                  item.scheduleDetails.scheduleData.selectedSlots
                );
              }
              
              // Ensure dates are properly converted
              if (item.createdAt) {
                item.createdAt = new Date(item.createdAt);
              }
              if (item.updatedAt) {
                item.updatedAt = new Date(item.updatedAt);
              }
              
              // Ensure arrays and objects have default values
              if (!item.creativeIds) {
                item.creativeIds = [];
              }
              if (!item.creativeDurations) {
                item.creativeDurations = {};
              }
              
              return item;
            }
            return null;
          })
          .filter((item): item is Playlist => item !== null);
      }
    }
  } catch (error) {
    console.error("Error loading playlists from localStorage:", error);
  }
  return [];
};

const saveToStorage = (playlists: Playlist[]): void => {
  try {
    // Convert Sets to arrays and dates to strings before saving to localStorage
    const playlistsToSave = playlists.map((playlist) => {
      let processedPlaylist = { ...playlist };
      
      // Convert Set to Array for selectedSlots if it exists
      if (playlist.scheduleDetails?.scheduleData?.selectedSlots instanceof Set) {
        processedPlaylist = {
          ...processedPlaylist,
          scheduleDetails: {
            ...playlist.scheduleDetails,
            scheduleData: {
              ...playlist.scheduleDetails.scheduleData,
              selectedSlots: Array.from(playlist.scheduleDetails.scheduleData.selectedSlots),
            },
          },
        };
      }
      
      return processedPlaylist;
    });
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(playlistsToSave));
  } catch (error) {
    console.error("Error saving playlists to localStorage:", error);
  }
};

export const PlaylistProvider: React.FC<PlaylistProviderProps> = ({
  children,
}) => {
  // Initialize with empty array, will be loaded from localStorage
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const loadedPlaylists = loadFromStorage();
    setPlaylists(loadedPlaylists);
    setIsLoaded(true);
  }, []);

  // Save to localStorage whenever playlists change (but only after initial load)
  useEffect(() => {
    if (isLoaded) {
      saveToStorage(playlists);
    }
  }, [playlists, isLoaded]);

  const addPlaylist = (title: string): Playlist => {
    const newPlaylist: Playlist = {
      id: Date.now().toString(),
      title,
      creativesCount: 0,
      // Remove default imageUrl - will be computed dynamically
      status: "Draft Saved",
      isScheduled: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      creativeIds: [],
      creativeDurations: {},
    };

    setPlaylists((prev) => {
      const updated = [...prev, newPlaylist];
      return updated;
    });
    return newPlaylist;
  };

  const updatePlaylist = (id: string, updates: Partial<Playlist>) => {
    setPlaylists((prev) => {
      const updated = prev.map((playlist) =>
        playlist.id === id
          ? { ...playlist, ...updates, updatedAt: new Date() }
          : playlist
      );
      return updated;
    });
  };

  const updatePlaylistCreatives = (
    id: string,
    creativeIds: string[],
    creativeDurations: Record<string, number> = {},
    allCreatives: any[] = [] // Add allCreatives parameter
  ) => {
    setPlaylists((prev) => {
      const updated = prev.map((playlist) => {
        if (playlist.id === id) {
          // Don't set imageUrl here - it will be computed dynamically
          const updatedPlaylist = {
            ...playlist,
            creativeIds,
            creativeDurations: {
              ...playlist.creativeDurations,
              ...creativeDurations,
            },
            creativesCount: creativeIds.length,
            updatedAt: new Date(),
          };

          return updatedPlaylist;
        }
        return playlist;
      });
      return updated;
    });
  };

  const deletePlaylist = (id: string) => {
    setPlaylists((prev) => {
      const updated = prev.filter((playlist) => playlist.id !== id);
      return updated;
    });
  };

  const getPlaylistById = (id: string) => {
    return playlists.find((playlist) => playlist.id === id);
  };

  // New method to get playlist with dynamic image from first creative
  const getPlaylistWithImage = (id: string, allCreatives: any[]) => {
    const playlist = getPlaylistById(id);
    if (!playlist) return undefined;

    // If playlist has creatives, use the first creative's image
    if (playlist.creativeIds.length > 0 && allCreatives.length > 0) {
      const firstCreative = allCreatives.find(
        creative => creative.id === playlist.creativeIds[0]
      );
      
      if (firstCreative && firstCreative.imageUrl) {
        return {
          ...playlist,
          imageUrl: firstCreative.imageUrl
        };
      }
    }

    // Return playlist without imageUrl if no creatives
    return playlist;
  };

  // Enhanced schedulePlaylist function with comprehensive data handling and debugging
  const schedulePlaylist = (id: string, scheduleDetails?: PlaylistScheduleDetails) => {
    console.log("=== PLAYLIST CONTEXT: schedulePlaylist called ===");
    console.log("Playlist ID:", id);
    console.log("Schedule Details received:", scheduleDetails);
    
    // CRITICAL FIX: Don't overwrite existing schedule details if none provided
    const currentPlaylist = playlists.find(p => p.id === id);
    if (!scheduleDetails && currentPlaylist?.scheduleDetails) {
      console.log("=== PREVENTING OVERWRITE ===");
      console.log("No new schedule details provided, but playlist already has schedule details");
      console.log("Existing schedule details:", currentPlaylist.scheduleDetails);
      console.log("Only updating status, keeping existing schedule details");
      
      updatePlaylist(id, {
        status: "Playing",
        isScheduled: true,
        scheduledAt: currentPlaylist.scheduledAt || new Date().toISOString(),
        // Keep existing schedule details
        scheduleDetails: currentPlaylist.scheduleDetails,
      });
      return;
    }

    const scheduledAt = new Date().toISOString();
    
    // Prepare schedule details with proper data conversion and enhanced storage
    let processedScheduleDetails = scheduleDetails;
    if (scheduleDetails) {
      // Convert Set to Array for storage
      if (scheduleDetails.scheduleData?.selectedSlots instanceof Set) {
        processedScheduleDetails = {
          ...scheduleDetails,
          scheduleData: {
            ...scheduleDetails.scheduleData,
            selectedSlots: Array.from(scheduleDetails.scheduleData.selectedSlots)
          },
          scheduledAt
        };
      } else {
        processedScheduleDetails = {
          ...scheduleDetails,
          scheduledAt
        };
      }

      // Log the comprehensive schedule details for debugging
      console.log("=== PROCESSED SCHEDULE DETAILS ===");
      console.log("processedScheduleDetails:", processedScheduleDetails);
      console.log("Schedule Type:", processedScheduleDetails.scheduleType);
      console.log("Announcement:", processedScheduleDetails.announcement);
      console.log("Schedule Data:", processedScheduleDetails.scheduleData);
      console.log("Announcement Details:", processedScheduleDetails.announcementDetails);
    }

    console.log("=== UPDATING PLAYLIST ===");
    console.log("Setting status to Playing, isScheduled to true");
    console.log("Setting scheduledAt to:", scheduledAt);
    console.log("Setting scheduleDetails to:", processedScheduleDetails);

    updatePlaylist(id, {
      status: "Playing",
      isScheduled: true,
      scheduledAt,
      scheduleDetails: processedScheduleDetails,
    });

    console.log("=== PLAYLIST UPDATE COMPLETE ===");
    
    // Verify the playlist was updated
    setTimeout(() => {
      const updatedPlaylist = playlists.find(p => p.id === id);
      console.log("=== VERIFICATION: Updated playlist ===");
      console.log("Updated playlist:", updatedPlaylist);
      console.log("Updated playlist scheduleDetails:", updatedPlaylist?.scheduleDetails);
    }, 100);
  };

  // Method to get playlist with creative details
  const getPlaylistWithCreatives = (id: string, allCreatives: any[]) => {
    const playlist = getPlaylistById(id);
    
    if (!playlist) {
      return { playlist: undefined, creativesWithDurations: [] };
    }

    const creativesWithDurations = playlist.creativeIds
      .map(creativeId => {
        const creative = allCreatives.find(c => c.id === creativeId);
        if (!creative) return null;

        return {
          ...creative,
          duration: playlist.creativeDurations[creativeId] || creative.duration || 10
        };
      })
      .filter(Boolean);

    return { playlist, creativesWithDurations };
  };

  // Add method to clear all playlists (similar to ScheduledCreativesContext)
  const clearAllPlaylists = () => {
    setPlaylists([]);
  };

  return (
    <PlaylistContext.Provider
      value={{
        playlists,
        addPlaylist,
        updatePlaylist,
        deletePlaylist,
        getPlaylistById,
        schedulePlaylist,
        updatePlaylistCreatives,
        getPlaylistWithCreatives,
        getPlaylistWithImage,
        clearAllPlaylists,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};